import React, { useEffect, useState } from 'react';
import { useAdminAuth } from '../../context/AdminAuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Plus, Trash2, Image as ImageIcon } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const GalleryManagement = () => {
  const { getAuthHeaders } = useAdminAuth();
  const { toast } = useToast();
  const [galleries, setGalleries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadedPhotos, setUploadedPhotos] = useState([]);

  const [formData, setFormData] = useState({
    title: '',
    title_english: '',
    description: '',
    description_english: '',
    category: 'events',
    category_bengali: 'অনুষ্ঠান',
    location: 'Agartala',
  });

  useEffect(() => {
    fetchGalleries();
  }, []);

  const fetchGalleries = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/galleries`, getAuthHeaders());
      setGalleries(response.data);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to fetch galleries', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);

    try {
      const uploadPromises = files.map(async (file) => {
        const uploadFormData = new FormData();
        uploadFormData.append('file', file);
        uploadFormData.append('category', 'galleries');

        const response = await axios.post(
          `${API_URL}/api/admin/upload/image`,
          uploadFormData,
          {
            headers: {
              ...getAuthHeaders().headers,
              'Content-Type': 'multipart/form-data',
            },
          }
        );
        return {
          url: response.data.url,
          caption: '',
          caption_english: '',
        };
      });

      const photos = await Promise.all(uploadPromises);
      setUploadedPhotos([...uploadedPhotos, ...photos]);
      toast({ title: 'Success', description: `${files.length} photos uploaded` });
    } catch (error) {
      toast({ title: 'Error', description: 'Photo upload failed', variant: 'destructive' });
    } finally {
      setUploading(false);
    }
  };

  const updatePhotoCaption = (index, field, value) => {
    const updated = [...uploadedPhotos];
    updated[index][field] = value;
    setUploadedPhotos(updated);
  };

  const removePhoto = (index) => {
    setUploadedPhotos(uploadedPhotos.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (uploadedPhotos.length === 0) {
      toast({ title: 'Error', description: 'Please upload at least one photo', variant: 'destructive' });
      return;
    }

    setLoading(true);
    try {
      await axios.post(
        `${API_URL}/api/admin/galleries`,
        { ...formData, photos: uploadedPhotos },
        getAuthHeaders()
      );
      toast({ title: 'Success', description: 'Gallery created successfully' });
      resetForm();
      fetchGalleries();
      setShowModal(false);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to create gallery', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this gallery?')) return;

    try {
      await axios.delete(`${API_URL}/api/admin/galleries/${id}`, getAuthHeaders());
      toast({ title: 'Success', description: 'Gallery deleted successfully' });
      fetchGalleries();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to delete gallery', variant: 'destructive' });
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      title_english: '',
      description: '',
      description_english: '',
      category: 'events',
      category_bengali: 'অনুষ্ঠান',
      location: 'Agartala',
    });
    setUploadedPhotos([]);
  };

  const categories = [
    { value: 'events', bengali: 'অনুষ্ঠান' },
    { value: 'culture', bengali: 'সংস্কৃতি' },
    { value: 'politics', bengali: 'রাজনীতি' },
    { value: 'sports', bengali: 'খেলাধুলা' },
    { value: 'nature', bengali: 'প্রকৃতি' },
    { value: 'heritage', bengali: 'ঐতিহ্য' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Photo Gallery Management</h1>
          <p className="text-gray-600 mt-1">Manage photo albums and galleries</p>
        </div>
        <Button onClick={() => { resetForm(); setShowModal(true); }} className="bg-red-600 hover:bg-red-700">
          <Plus className="w-4 h-4 mr-2" />
          Create New Gallery
        </Button>
      </div>

      {/* Galleries Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {loading && galleries.length === 0 ? (
          <div className="col-span-full text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
          </div>
        ) : galleries.length === 0 ? (
          <Card className="col-span-full">
            <CardContent className="py-12 text-center">
              <ImageIcon className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500">No galleries yet. Create your first gallery!</p>
            </CardContent>
          </Card>
        ) : (
          galleries.map((gallery) => (
            <Card key={gallery.id} className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="relative h-48 -mx-6 -mt-6 mb-4 overflow-hidden rounded-t-lg">
                  <img
                    src={gallery.photos[0]?.url}
                    alt={gallery.title_english}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-2 right-2">
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => handleDelete(gallery.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                <CardTitle className="text-lg">{gallery.title}</CardTitle>
                <div className="flex items-center gap-2 mt-2">
                  <Badge>{gallery.category_bengali}</Badge>
                  <span className="text-sm text-gray-500">{gallery.location}</span>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600 mb-2">{gallery.description}</p>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">{gallery.photos.length} photos</span>
                  <span className="text-gray-500">{gallery.date}</span>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Create Gallery Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create New Gallery</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Title (Bengali) *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label>Title (English) *</Label>
                <Input
                  value={formData.title_english}
                  onChange={(e) => setFormData({ ...formData, title_english: e.target.value })}
                  required
                />
              </div>
            </div>

            <div>
              <Label>Description (Bengali) *</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={2}
                required
              />
            </div>

            <div>
              <Label>Description (English) *</Label>
              <Textarea
                value={formData.description_english}
                onChange={(e) => setFormData({ ...formData, description_english: e.target.value })}
                rows={2}
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Category *</Label>
                <select
                  value={formData.category}
                  onChange={(e) => {
                    const cat = categories.find(c => c.value === e.target.value);
                    setFormData({ ...formData, category: e.target.value, category_bengali: cat.bengali });
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  {categories.map((cat) => (
                    <option key={cat.value} value={cat.value}>
                      {cat.bengali} - {cat.value}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Location *</Label>
                <Input
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  required
                />
              </div>
            </div>

            <div>
              <Label>Upload Photos *</Label>
              <Input type="file" accept="image/*" multiple onChange={handlePhotoUpload} />
              {uploading && <p className="text-sm text-gray-500 mt-1">Uploading photos...</p>}
            </div>

            {/* Uploaded Photos */}
            {uploadedPhotos.length > 0 && (
              <div className="space-y-3">
                <Label>Photos ({uploadedPhotos.length})</Label>
                {uploadedPhotos.map((photo, index) => (
                  <div key={index} className="flex gap-3 p-3 border rounded-lg">
                    <img src={photo.url} alt="" className="w-24 h-24 object-cover rounded" />
                    <div className="flex-1 space-y-2">
                      <Input
                        placeholder="Caption (Bengali)"
                        value={photo.caption}
                        onChange={(e) => updatePhotoCaption(index, 'caption', e.target.value)}
                      />
                      <Input
                        placeholder="Caption (English)"
                        value={photo.caption_english}
                        onChange={(e) => updatePhotoCaption(index, 'caption_english', e.target.value)}
                      />
                    </div>
                    <Button type="button" variant="destructive" size="sm" onClick={() => removePhoto(index)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}

            <div className="flex gap-2 justify-end">
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-red-600 hover:bg-red-700" disabled={loading}>
                {loading ? 'Creating...' : 'Create Gallery'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GalleryManagement;